/**
 * "Create with Mochi" opened from another screen (Files, an editor's home):
 * the design's Create page (weco2/files-mochi/fm-create.jsx) as a full-window
 * layer over the current screen, and a host that opens it on
 * `openMochiCreate()`.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

import { MOCHI_CREATE_EVENT, type CreateKind, type MochiCreateRequest } from "./kinds";
import { MochiCreateChat } from "./MochiCreateChat";

export interface MochiCreateDialogProps {
  open: boolean;
  onClose: () => void;
  kind?: CreateKind;
  initialFileIds?: string[];
}

export function MochiCreateDialog({ open, onClose, kind, initialFileIds }: MochiCreateDialogProps) {
  const [current, setCurrent] = useState<CreateKind>(kind ?? "presentation");
  useEffect(() => {
    if (kind) setCurrent(kind);
  }, [kind]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      // The voice call and the Files picker close themselves first.
      if (e.key === "Escape" && !document.querySelector(".fmv, [data-fm-picker]")) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mochi-create-dialog-h"
      className="fm-create-page flex flex-col"
    >
      <MochiCreateChat
        kind={current}
        onKindChange={setCurrent}
        initialFileIds={initialFileIds}
        onClose={onClose}
        onCreated={onClose}
        headingId="mochi-create-dialog-h"
        className="h-full"
      />
    </div>,
    document.body,
  );
}

/** Mount once (inside the router); opens the dialog on `openMochiCreate()`. */
export function MochiCreateHost() {
  const [request, setRequest] = useState<MochiCreateRequest | null>(null);
  useEffect(() => {
    const onOpen = (e: Event) => setRequest((e as CustomEvent<MochiCreateRequest>).detail ?? {});
    window.addEventListener(MOCHI_CREATE_EVENT, onOpen);
    return () => window.removeEventListener(MOCHI_CREATE_EVENT, onOpen);
  }, []);
  return (
    <MochiCreateDialog
      open={request !== null}
      onClose={() => setRequest(null)}
      kind={request?.kind}
      initialFileIds={request?.fileIds}
    />
  );
}
